import clsx from 'clsx';
import { Trans } from '@lingui/react/macro';
import logoImg from '../../assets/img/logo.svg';
import { useCurrentUser } from '../../services/userHooks';

interface GuestHeaderProps {
  showAuthButtons?: boolean;
  // When false, always render the logged-out Sign In / Sign Up controls and
  // never morph to "Open App". The landing page sets this since it only ever
  // renders for logged-out visitors (the `/` gate redirects logged-in users to
  // /home before the landing page mounts).
  enableSessionMorph?: boolean;
  // When false, omit the Sign Up button and show only Sign In. The landing page
  // sets this because its hero already carries the "Create an Account" CTA.
  showSignUp?: boolean;
  className?: string;
}

function GuestHeader({
  showAuthButtons = true,
  enableSessionMorph = true,
  showSignUp = true,
  className,
}: GuestHeaderProps) {
  return (
    <header
      className={clsx(
        // guest-header-glass (static.css) makes this an iOS-style Liquid Glass
        // bar; sticky top-0 + z-header give the glass pseudo its stacking context.
        'guest-header-glass z-header sticky top-0 flex items-center justify-between px-5 py-4',
        className,
      )}
    >
      {/* Progressive "Liquid Glass" blur: a stack of backdrop-filter layers of
          increasing radius, each masked to a band (see .guest-header-glass in
          static.css) so the blur ramps from sharp at the bottom edge to heavily
          blurred at the top. Purely decorative, so hidden from assistive tech. */}
      <div className="guest-header-glass__blur" aria-hidden="true">
        <div className="guest-header-glass__layer" />
        <div className="guest-header-glass__layer" />
        <div className="guest-header-glass__layer" />
        <div className="guest-header-glass__layer" />
        <div className="guest-header-glass__layer" />
        <div className="guest-header-glass__layer" />
      </div>

      {/* Logo */}
      <div className="flex items-center">
        <a href="/" className="flex items-center">
          <img src={logoImg} alt="Instapaper" className="app-logo" />
        </a>
      </div>

      {/* Auth Buttons */}
      {showAuthButtons &&
        (enableSessionMorph ? (
          <MorphingAuthControls showSignUp={showSignUp} />
        ) : (
          <LoggedOutAuthControls showSignUp={showSignUp} />
        ))}
    </header>
  );
}

function MorphingAuthControls({ showSignUp = true }: { showSignUp?: boolean }) {
  // Morph the auth controls based on session: once the current user resolves,
  // the Sign In / Sign Up pair collapses to a single "Open App" link pointing
  // at the authenticated home. While the query is loading (and in the
  // prerendered/first-paint state) we render the logged-out controls so the
  // common anonymous visit shows no flash.
  const { currentUser } = useCurrentUser();

  if (currentUser) {
    return (
      <div className="flex items-center">
        <a href="/home" className="button-primary">
          <Trans>Open App</Trans>
        </a>
      </div>
    );
  }

  return <LoggedOutAuthControls showSignUp={showSignUp} />;
}

function LoggedOutAuthControls({ showSignUp = true }: { showSignUp?: boolean }) {
  return (
    <div className="flex items-center space-x-3">
      <a
        href="/user/login?next=/home"
        className="text-copy-secondary hover:text-link text-sm font-medium transition-colors"
      >
        <Trans>Sign In</Trans>
      </a>
      {showSignUp && (
        <a href="/user/register" className="button-primary">
          <Trans>Sign Up</Trans>
        </a>
      )}
    </div>
  );
}

export default GuestHeader;
